/**
 * AK-711 — словарь фильтра «Статус посева» на вкладке «Поля».
 *
 * Что было (прод v1.1746, жалоба founder «откуда тут растёт так много
 * статусов… этот фильтр вообще не работает»): выпадашка строилась перебором
 * `SOWING_STATUS_RU` — а это словарь ТОНОВ БЕЙДЖА, то есть всё пространство
 * кодов, которое сервер может прислать на ЧТЕНИЕ (14 штук: канон 4 + легаси +
 * вычисляемые уточнения полноты площади ADR-055 §D2). Подписи у них — канон
 * ADR-059 §3 из четырёх слов, поэтому список печатал «Растёт» четыре раза и
 * «Убрано» дважды, а выбор большинства пунктов не находил ничего: в
 * `sowings.status` после конвертации ADR-063 §R9 живут ровно
 * planned/growing/harvested/written_off и NULL (узкий CHECK
 * `sowings_status_check`).
 *
 * Ровно тем же болел ручной выбор статуса — вылечено раньше (AK-513,
 * `components/fields/SowingStatusBadge.tsx`): меню записи схлопнули до канона
 * 4. Фильтр чтения тогда не тронули; здесь — та же операция для него.
 *
 * Два правила:
 *   1. **Одна подпись — один пункт.** Группы выводятся ИЗ словаря подписей
 *      (`SOWING_STATUS_RU`), а не переписываются руками: добавят завтра ещё
 *      один код со словом «Растёт» — он приклеится к существующей группе, а не
 *      создаст пятый «Растёт». Пункт фильтрует по ВСЕЙ группе кодов сразу.
 *   2. **Не предлагать пустое.** Список пунктов = то, что реально есть в
 *      выборке (`GET /fields/sowing-status-facets`), со счётчиком. Дисейбл
 *      вместо скрытия не годится: 10 из 14 пунктов были бы вечно серыми —
 *      это шум, а не информация.
 */
import { SOWING_STATUS_RU } from '@/components/fields/sowingStatus';
import { UNREALIZED_SOWING_LABEL } from '@/lib/sowings/seasonDisplay';

/** Корзина «занятия нет»: посев с `status IS NULL` либо поля без посева. */
export const NO_SOWING_CODE = 'unplanned';

export interface SowingStatusGroup {
  /** Значение для `?sowing_status=` — CSV кодов группы. */
  value: string;
  label: string;
  codes: string[];
}

/** Счётчик из `/fields/sowing-status-facets`. `status: null` = «занятия нет». */
export interface SowingStatusFacet {
  status: string | null;
  fields: number;
}

/**
 * Коды с ОДИНАКОВОЙ подписью → одна группа. Порядок групп — порядок первого
 * появления кода в словаре тонов (он идёт по ходу сезона).
 */
export function buildSowingStatusGroups(): SowingStatusGroup[] {
  const byLabel = new Map<string, SowingStatusGroup>();
  for (const [code, info] of Object.entries(SOWING_STATUS_RU)) {
    const found = byLabel.get(info.label);
    if (found) {
      found.codes.push(code);
      found.value = found.codes.join(',');
    } else {
      byLabel.set(info.label, { value: code, label: info.label, codes: [code] });
    }
  }
  return [...byLabel.values()];
}

export const SOWING_STATUS_GROUPS: SowingStatusGroup[] = buildSowingStatusGroups();

/** Группа, в которую входит код (или null — код вне словаря подписей). */
function groupForCode(code: string): SowingStatusGroup | null {
  return SOWING_STATUS_GROUPS.find((g) => g.codes.includes(code)) ?? null;
}

/**
 * Подпись выбранного пункта по его `value`.
 *
 * Нужна кнопке фильтра: счётчики приезжают лениво (по открытию списка), а
 * показывать, чем сужен список, надо СРАЗУ — иначе после перезагрузки страницы
 * кнопка врёт «Все статусы» при активном фильтре.
 */
export function sowingStatusFilterLabel(
  value: string | null | undefined,
  opts?: { seasonCompleted?: boolean },
): string | null {
  if (!value) return null;
  const group = SOWING_STATUS_GROUPS.find((g) => g.value === value);
  if (!group) return null;
  if (opts?.seasonCompleted && group.codes.includes('planned')) {
    return UNREALIZED_SOWING_LABEL;
  }
  return group.label;
}

export interface SowingStatusOption extends SowingStatusGroup {
  /** Сколько полей в выборке стоит в этой группе. */
  count: number;
}

/**
 * Пункты фильтра из фасетов сервера.
 *
 * · подписи не дублируются — коды одной подписи уже склеены в группу;
 * · пункта без данных нет вовсе (пустых вариантов не предлагаем);
 * · в завершённом сезоне «Запланировано» читается «Не реализован» — тем же
 *   словом, что и бейдж в таблице (`resolveSowingDisplay`), иначе пункт и
 *   строка спорят друг с другом;
 * · фасеты ещё не приехали (`null`) → пунктов нет, показываем только «Все
 *   статусы»: лучше ничего, чем заведомо пустой список кодов;
 * · `keepValue` — уже выбранный пункт, которого в новых фасетах не стало
 *   (сузили хозяйство). Он остаётся в списке со счётчиком 0, иначе выпадашка
 *   молча забывает, чем именно сужен список, и «ноль строк» выглядит поломкой.
 */
export function buildSowingStatusOptions(
  facets: SowingStatusFacet[] | null | undefined,
  opts?: { seasonCompleted?: boolean; keepValue?: string },
): SowingStatusOption[] {
  if (!facets) return [];
  const acc = new Map<string, SowingStatusOption>();
  for (const facet of facets) {
    if (!facet.fields) continue;
    const code = facet.status ?? NO_SOWING_CODE;
    const group = groupForCode(code);
    if (!group) continue;
    const seen = acc.get(group.value);
    if (seen) {
      seen.count += facet.fields;
    } else {
      acc.set(group.value, { ...group, count: facet.fields });
    }
  }
  if (opts?.keepValue && !acc.has(opts.keepValue)) {
    const kept = SOWING_STATUS_GROUPS.find((g) => g.value === opts.keepValue);
    if (kept) acc.set(kept.value, { ...kept, count: 0 });
  }
  const options = [...acc.values()];
  // Порядок групп фиксирован словарём — сортируем по нему, а не по счётчику:
  // прыгающий между запросами список сложнее читать, чем неоптимальный.
  options.sort(
    (a, b) =>
      SOWING_STATUS_GROUPS.findIndex((g) => g.value === a.value) -
      SOWING_STATUS_GROUPS.findIndex((g) => g.value === b.value),
  );
  if (opts?.seasonCompleted) {
    return options.map((o) =>
      o.codes.includes('planned') ? { ...o, label: UNREALIZED_SOWING_LABEL } : o,
    );
  }
  return options;
}
